import {
  Archive,
  ArrowCounterClockwise,
  ArrowsOutCardinal,
  Copy,
  DownloadSimple,
  Files,
  FolderPlus,
  LinkSimple,
  PencilSimple,
  Plus,
  ShareNetwork,
  Star,
  StarHalf,
  Trash,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { separator } from "~/actions/adapters/menu";
import { createAction } from "~/actions/registry";
import type { Action, ActionContext } from "~/actions/types";
import { EXPORT_FORMATS, type ExportFormat } from "~/api/libraryTypes";
import { exportDocumentFile } from "~/api/queries";
import { ABILITY } from "~/api/types";
import { CURRENT_WORKSPACE } from "~/policies/usePolicy";

const section = (ctx: { t: (key: string) => string }) => ctx.t("Documents");

/**
 * Format names, translated. A record rather than `t(format)` because the KEYS are wire values
 * (`markdown`, `html`, `json`) and a catalogue keyed on those would be a catalogue of protocol
 * strings — the thing `i18nGuards/dynamicKeys.ts` exists to keep out.
 */
const FORMAT_LABELS: Record<ExportFormat, (t: (key: string) => string) => string> = {
  markdown: (t) => t("Markdown"),
  html: (t) => t("HTML"),
  json: (t) => t("JSON"),
};

/** Entity-scoped actions: without a target there is nothing to act on. */
function target(ctx: ActionContext): string | undefined {
  return ctx.activeEntityId;
}

function canOn(ctx: ActionContext, ability: string): boolean {
  const id = target(ctx);
  return id !== undefined && ctx.abilities(id)[ability] === true;
}

export const newDocument = createAction({
  id: "documents.new",
  name: (ctx) => ctx.t("New document…"),
  section,
  icon: Plus,
  keywords: "create add page doc",
  visible: (ctx) => ctx.abilities(CURRENT_WORKSPACE)[ABILITY.workspaceCreateDocument] === true,
  perform: (ctx) => ctx.shell.getState().openDialog({ kind: "newDocument" }),
});

export const newCollection = createAction({
  id: "collections.new",
  name: (ctx) => ctx.t("New collection…"),
  section: (ctx) => ctx.t("Collections"),
  icon: FolderPlus,
  keywords: "create add folder space",
  visible: (ctx) => ctx.abilities(CURRENT_WORKSPACE)[ABILITY.workspaceCreateCollection] === true,
  perform: (ctx) => ctx.shell.getState().openDialog({ kind: "newCollection" }),
});

export const renameDocument = createAction({
  id: "documents.rename",
  name: (ctx) => ctx.t("Rename…"),
  section,
  icon: PencilSimple,
  visible: (ctx) => canOn(ctx, ABILITY.documentUpdate),
  perform: (ctx) => {
    const id = target(ctx);
    if (id) {
      ctx.shell.getState().openDialog({ kind: "renameDocument", documentId: id });
    }
  },
});

export const duplicateDocument = createAction({
  id: "documents.duplicate",
  name: (ctx) => ctx.t("Duplicate"),
  section,
  icon: Copy,
  visible: (ctx) =>
    target(ctx) !== undefined &&
    ctx.abilities(CURRENT_WORKSPACE)[ABILITY.workspaceCreateDocument] === true,
  perform: async (ctx) => {
    const id = target(ctx);
    if (!id) {
      return;
    }
    const original = await ctx.api.documents.get(id);
    const copy = await ctx.api.documents.create({
      title: ctx.t("{{title}} (copy)", { title: original.title }),
      collectionId: original.collectionId,
      // W10: the body was being dropped — a "duplicate" that produced an empty document.
      // `documents.create` has carried `text` since W7; this is the first caller to use it here.
      text: original.text,
    });
    toast.success(ctx.t("Duplicated to “{{title}}”", { title: copy.title }));
  },
});

/**
 * W10 §1 — move. `documents.move` requires `Update` on the document AND on the destination
 * collection, so the ENTRY gate is the document's own update ability and the destination list
 * inside the dialog is filtered by each collection's — one gate cannot answer both questions.
 */
export const moveDocument = createAction({
  id: "documents.move",
  name: (ctx) => ctx.t("Move…"),
  section,
  icon: ArrowsOutCardinal,
  keywords: "collection folder nest parent relocate",
  visible: (ctx) => canOn(ctx, ABILITY.documentUpdate),
  perform: (ctx) => {
    const id = target(ctx);
    if (id) {
      ctx.shell.getState().openDialog({ kind: "moveDocument", documentId: id });
    }
  },
});

/**
 * W10 §2 — star / unstar. TWO actions rather than one that renames itself: an action's icon is
 * static, and ⌘K showing a ☆ next to the word "Unstar" is the kind of small lie that makes a
 * palette untrustworthy. Exactly one of the pair is ever visible.
 *
 * Neither is gated on an ability: a star is the VIEWER's private bookmark, and the server checks
 * nothing beyond the session (`StarsController` never consults the document's policy). Gating it
 * on `update` would hide the bookmark from precisely the read-only reader who most wants one.
 */
export const starDocument = createAction({
  id: "documents.star",
  name: (ctx) => ctx.t("Star"),
  section,
  icon: Star,
  keywords: "favourite favorite bookmark starred",
  visible: (ctx) => {
    const id = target(ctx);
    return id !== undefined && ctx.stars !== undefined && ctx.stars.idFor(id) === undefined;
  },
  perform: (ctx) => {
    const id = target(ctx);
    if (id) {
      ctx.stars?.toggle({ documentId: id });
    }
  },
});

export const unstarDocument = createAction({
  id: "documents.unstar",
  name: (ctx) => ctx.t("Unstar"),
  section,
  icon: StarHalf,
  keywords: "favourite favorite bookmark starred remove",
  visible: (ctx) => {
    const id = target(ctx);
    return id !== undefined && ctx.stars !== undefined && ctx.stars.idFor(id) !== undefined;
  },
  perform: (ctx) => {
    const id = target(ctx);
    if (id) {
      ctx.stars?.toggle({ documentId: id });
    }
  },
});

export const shareDocument = createAction({
  id: "documents.share",
  name: (ctx) => ctx.t("Share…"),
  section,
  icon: ShareNetwork,
  visible: (ctx) => canOn(ctx, ABILITY.documentShare),
  perform: (ctx) => {
    const id = target(ctx);
    if (id) {
      ctx.shell.getState().openDialog({ kind: "shareDocument", documentId: id });
    }
  },
});

/**
 * W10 §5 — download. One parent with a child per format, because the FORMAT is the only choice
 * and a dialog for one select is a dialog too many.
 *
 * `EXPORT_FORMATS` is the closed set the platform's `ParseFileOperationFormat` really knows;
 * anything else falls through its switch to markdown, which is why there is no PDF entry — a
 * "PDF" here would hand back markdown under a `.pdf` name. The artefact is a **zip** in every
 * case, and the toast says so rather than promising a `.md`.
 *
 * Export is a background job, so the toast is a promise-toast: it stays until the job settles
 * (or its three-minute ceiling), because a silent thirty-second wait reads as a dead button.
 */
export const downloadDocument = createAction({
  id: "documents.download",
  name: (ctx) => ctx.t("Download"),
  section,
  icon: DownloadSimple,
  keywords: "export save markdown html json zip",
  visible: (ctx) => target(ctx) !== undefined,
  children: (ctx) =>
    EXPORT_FORMATS.map((format) =>
      createAction({
        id: `documents.download.${format}`,
        name: () => FORMAT_LABELS[format](ctx.t),
        section,
        perform: async (inner) => {
          const id = target(inner);
          if (!id) {
            return;
          }
          // The job can run for tens of seconds; a silent wait reads as a dead button.
          const pending = toast.loading(inner.t("Preparing your download…"));
          try {
            const document = await inner.api.documents.get(id);
            await exportDocumentFile(
              inner.api,
              { documentId: id, format, title: document.title },
              (key) => inner.t(key),
            );
            toast.success(inner.t("Downloaded as a zip"), { id: pending });
          } catch (error) {
            toast.error(error instanceof Error ? error.message : inner.t("The export failed"), {
              id: pending,
            });
          }
        },
      }),
    ),
});

/**
 * W10 §5 — copy link. No endpoint at all: the document's own in-app address is the link, and
 * the `/s/…` share URL is a different thing entirely (§4, and it may not exist).
 */
export const copyDocumentLink = createAction({
  id: "documents.copyLink",
  name: (ctx) => ctx.t("Copy link"),
  section,
  icon: LinkSimple,
  keywords: "url address clipboard",
  visible: (ctx) => target(ctx) !== undefined,
  perform: (ctx) => {
    const id = target(ctx);
    if (!id) {
      return;
    }
    const url = `${window.location.origin}/documents/${id}`;
    void navigator.clipboard?.writeText(url);
    toast.success(ctx.t("Link copied"));
  },
});

/**
 * W10 §9 — save the document as a template.
 *
 * A COPY becomes the template. `documents.templatize` would convert this very document — it
 * mutates rather than copies, and there is no un-templatize verb — so the destructive direction
 * lives in Settings → Templates behind a confirmation, and the everyday one is here and safe.
 */
export const saveAsTemplate = createAction({
  id: "documents.saveAsTemplate",
  name: (ctx) => ctx.t("Save as template"),
  section,
  icon: Files,
  keywords: "template starting point reuse boilerplate",
  visible: (ctx) =>
    target(ctx) !== undefined &&
    ctx.abilities(CURRENT_WORKSPACE)[ABILITY.workspaceCreateDocument] === true,
  perform: async (ctx) => {
    const id = target(ctx);
    if (!id) {
      return;
    }
    const original = await ctx.api.documents.get(id);
    const template = await ctx.api.templates.createFrom({
      documentId: id,
      title: original.title,
      text: original.text,
      collectionId: original.collectionId,
    });
    toast.success(ctx.t("“{{title}}” is available as a template", { title: template.title }));
  },
});

export const archiveDocument = createAction({
  id: "documents.archive",
  name: (ctx) => ctx.t("Archive"),
  section,
  icon: Archive,
  visible: (ctx) => canOn(ctx, ABILITY.documentArchive),
  perform: async (ctx) => {
    const id = target(ctx);
    if (!id) {
      return;
    }
    await ctx.api.documents.archive(id);
    toast.success(ctx.t("Document archived"));
  },
});

export const restoreDocument = createAction({
  id: "documents.restore",
  name: (ctx) => ctx.t("Restore"),
  section,
  icon: ArrowCounterClockwise,
  visible: (ctx) => canOn(ctx, ABILITY.documentRestore),
  perform: async (ctx) => {
    const id = target(ctx);
    if (!id) {
      return;
    }
    await ctx.api.documents.restore(id);
    toast.success(ctx.t("Document restored"));
  },
});

export const deleteDocument = createAction({
  id: "documents.delete",
  name: (ctx) => ctx.t("Delete…"),
  section,
  icon: Trash,
  dangerous: true,
  visible: (ctx) => canOn(ctx, ABILITY.documentDelete),
  perform: (ctx) => {
    const id = target(ctx);
    if (id) {
      ctx.shell.getState().openDialog({ kind: "deleteDocument", documentId: id });
    }
  },
});

/** Top-level (command bar, toolbars): what makes sense with nothing selected. */
export const documentActions: Action[] = [newDocument, newCollection];

/** Row context menu / overflow menu, in menu order. */
export const documentRowActions: Action[] = [
  starDocument,
  unstarDocument,
  renameDocument,
  duplicateDocument,
  moveDocument,
  shareDocument,
  copyDocumentLink,
  downloadDocument,
  saveAsTemplate,
  separator("documents"),
  archiveDocument,
  restoreDocument,
  deleteDocument,
];
